/** Client 360 (spec §23–§33, Phase 7). Three separate scores, never blended. */
import { useState } from "react";
import { useParams } from "react-router-dom";
import type { EChartsOption } from "echarts";

import { Chart } from "@/components/charts/Chart";
import { Disclosure } from "@/components/cards/Disclosure";
import { JourneyTimeline } from "@/components/timeline/JourneyTimeline";
import { ClientFactors } from "@/features/clients/ClientFactors";
import { DataTable, type Column } from "@/components/tables/DataTable";
import { useClientProfile, useTimeline } from "@/hooks/useAnalytics";
import { count, inr, pct, shortDate } from "@/utils/format";

const TABS = ["Overview", "Timeline", "Factors", "Trends", "Relationships"] as const;
type Tab = (typeof TABS)[number];

interface ScorePayload {
  value: number; band: string; percentile: number | null;
  reasons: Array<{ direction: string; text: string; metric: string; value: unknown }>;
  components: Record<string, { raw: unknown; percentile: number; weight: number }>;
  suppressed: boolean;
}

export function ClientProfile() {
  const { clientId } = useParams<{ clientId: string }>();
  const [tab, setTab] = useState<Tab>("Overview");
  const { data, isLoading } = useClientProfile(clientId);
  // The period filter narrows the journey; this reveals the whole
  // relationship, which is the point of the view when it is what you want.
  const [allTime, setAllTime] = useState(false);
  const timeline = useTimeline("client", clientId, allTime);

  const summary = data?.summary as
    | {
        identity: Record<string, string | null>;
        engagement: Record<string, number | string | null>;
        business: Record<string, number | null>;
        scores: Record<string, ScorePayload>;
        segments: Record<string, string | null>;
      }
    | undefined;

  if (isLoading) return <div className="skeleton h-64" />;
  if (!summary) return <div className="card p-6">Client not found.</div>;

  const { identity, engagement, business, scores, segments } = summary;

  return (
    <div className="space-y-5">
      <header className="card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="label">{identity.referral_type} · {identity.area}</div>
            <h1 className="font-display font-bold text-[26px] tracking-tight mt-0.5">
              {identity.name}
            </h1>
            <p className="text-[13.5px] text-ink-muted mt-1">
              {identity.designation}
              {identity.institution ? ` · ${identity.institution}` : ""}
              {identity.commission_terms ? ` · ${identity.commission_terms}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {segments.rfm && (
              <span className="chip border-petrol/30 bg-petrol-soft text-petrol">
                RFM {segments.rfm}
              </span>
            )}
            {segments.lifecycle && (
              <span className="chip border-rule-strong bg-surface-alt text-ink-soft">
                {segments.lifecycle}
              </span>
            )}
            {identity.onboarding_state && (
              <span className="chip border-warn/30 bg-warn-soft text-warn">
                {identity.onboarding_state}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Three scores side by side — §27 forbids blending them. */}
      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
        <ScoreCard title="Health" subtitle="Is the relationship working now?"
                   score={scores.client_health} />
        <ScoreCard title="Opportunity" subtitle="Is it worth chasing?"
                   score={scores.client_opportunity} />
        <ScoreCard title="Risk" subtitle="Are we about to lose it?"
                   score={scores.client_risk} invert />
      </div>

      <nav className="flex gap-1 border-b border-rule">
        {TABS.map((name) => (
          <button
            key={name}
            onClick={() => setTab(name)}
            className={`px-3 py-2 text-[13.5px] border-b-2 -mb-px ${
              tab === name
                ? "border-petrol text-petrol font-medium"
                : "border-transparent text-ink-muted hover:text-ink"
            }`}
          >
            {name}
          </button>
        ))}
      </nav>

      {tab === "Overview" && (
        <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))" }}>
          <Panel title="Engagement" rows={[
            ["First activity", shortDate(engagement.first_activity as string)],
            ["Last activity", shortDate(engagement.last_activity as string)],
            ["Days since contact", engagement.days_since_activity ?? "—"],
            ["Days since referral", engagement.days_since_credit ?? "—"],
            ["Total activities", count(engagement.activities as number)],
            ["Positive responses", count(engagement.positive_responses as number)],
            ["Commitments", count(engagement.commitments as number)],
            ["Next follow-up", shortDate(engagement.next_followup as string)],
          ]} />
          <Panel title="Business" rows={[
            ["Leads", count(business.leads)],
            ["Admissions", count(business.admissions)],
            ["Conversion", pct(business.conversion_pct)],
            ["Hospital revenue", inr(business.revenue)],
            ["Commission approved", inr(business.commission_approved)],
            ["Commission net", inr(business.commission_net)],
            ["Credit rows", count(business.credits)],
          ]} />
        </div>
      )}

      {tab === "Factors" && clientId && (
        <ClientFactors clientId={clientId} />
      )}

      {tab === "Timeline" && (
        <JourneyTimeline
          allTime={allTime}
          onAllTimeChange={setAllTime}
          events={timeline.data?.events ?? []}
          mode="client"
          subject={identity.name ?? undefined}
          loading={timeline.isLoading}
        />
      )}

      {tab === "Trends" && <ClientTrend trend={data?.trend ?? []} />}

      {tab === "Relationships" && (
        <DataTable
          rows={(data?.breakdown ?? []) as Array<Record<string, never>>}
          columns={[
            { key: "label", header: "Employee" },
            { key: "activities", header: "Activities", align: "right" },
            { key: "positive_responses", header: "Positive", align: "right" },
            { key: "first_activity", header: "First", align: "right" },
            { key: "last_activity", header: "Last", align: "right" },
            { key: "credits", header: "Referrals", align: "right" },
            {
              key: "commission", header: "Commission", align: "right",
              render: (r: Record<string, never>) => inr(Number(r.commission)),
            },
          ] as Column<Record<string, never>>[]}
          rowKey={(r) => String((r as Record<string, string>).person_key)}
          exportName={`client-${clientId}-relationships`}
          footnote="Which employees have worked this client, and with what result — the view that shows whether a reassignment helped or hurt."
        />
      )}

      <Disclosure quality={data?.quality} metadata={data?.metadata} />
    </div>
  );
}

function ScoreCard({
  title, subtitle, score, invert = false,
}: {
  title: string;
  subtitle: string;
  score?: ScorePayload;
  /** Risk reads in reverse: a high number is bad, so the colour ramp flips. */
  invert?: boolean;
}) {
  if (!score) {
    return (
      <div className="card p-4">
        <div className="label">{title}</div>
        <p className="text-sm text-ink-muted mt-2">Not yet computed.</p>
      </div>
    );
  }
  const value = score.value;
  const good = invert ? value < 40 : value >= 60;
  const colour = good ? "#2F6B3C" : value >= (invert ? 20 : 40) ? "#8A6410" : "#9E3B2E";

  return (
    <div className="card p-4 space-y-2.5">
      <div>
        <div className="label">{title}</div>
        <p className="text-[11.5px] text-ink-faint">{subtitle}</p>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-display font-bold text-[34px] leading-none tnum" style={{ color: colour }}>
          {value.toFixed(0)}
        </span>
        <span className="text-sm text-ink-muted">/ 100</span>
        <span className="ml-auto chip border-rule-strong bg-surface-alt text-ink-soft">
          {score.band}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-surface-alt overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${value}%`, background: colour }} />
      </div>
      <ul className="space-y-1 text-[13px]">
        {score.reasons.slice(0, 4).map((reason, i) => (
          <li key={i} className={
            reason.direction === "positive" ? "text-good"
            : reason.direction === "negative" ? "text-bad" : "text-ink-muted"
          }>
            {reason.direction === "positive" ? "+" : reason.direction === "negative" ? "−" : "·"}{" "}
            {reason.text}
          </li>
        ))}
      </ul>
      {score.suppressed && (
        <p className="text-[11.5px] text-ink-faint">Sample too small to rank confidently.</p>
      )}
    </div>
  );
}

function Panel({ title, rows }: { title: string; rows: Array<[string, unknown]> }) {
  return (
    <div className="card p-4">
      <h3 className="font-display font-semibold text-[15px] mb-3">{title}</h3>
      <dl className="space-y-1.5">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-4 text-[13.5px]">
            <dt className="text-ink-muted">{label}</dt>
            <dd className="font-medium tnum">{String(value ?? "—")}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function ClientTrend({ trend }: { trend: Array<Record<string, unknown>> }) {
  const option: EChartsOption = {
    tooltip: { trigger: "axis" },
    legend: { data: ["Activities", "Referrals", "Revenue"] },
    xAxis: { type: "category", data: trend.map((t) => String(t.bucket)) },
    yAxis: [{ type: "value" }, { type: "value", axisLabel: { formatter: (v: number) => inr(v) } }],
    series: [
      { name: "Activities", type: "bar", barMaxWidth: 16, data: trend.map((t) => Number(t.activities ?? 0)) },
      { name: "Referrals", type: "bar", barMaxWidth: 16, data: trend.map((t) => Number(t.credits ?? 0)) },
      {
        name: "Revenue", type: "line", yAxisIndex: 1, smooth: true, showSymbol: false,
        data: trend.map((t) => Number(t.revenue ?? 0)),
      },
    ],
  };
  return (
    <div className="card p-4">
      <h3 className="font-display font-semibold text-[15px] mb-1">Twelve-month trend</h3>
      <p className="text-[12.5px] text-ink-muted mb-3">
        Effort and outcome on one axis pair — the gap between them is the story.
      </p>
      <Chart option={option} height={300} />
    </div>
  );
}
